import api from '../../../services/api';

// The "forever"/legacy-PaymentIntent-approach monthly donation creation
// branch of handleSubmit, extracted VERBATIM (the tail of `else if
// (donationType === 'monthly')`, reached for forever subscriptions or when
// the fixed-term PaymentMethod+schedule branch above didn't apply — handles
// when handleSubmit runs without an existing clientSecret).
export const createForeverSubscription = async ({
  monthlyAmt,
  forever,
  payments,
  firstName,
  lastName,
  email,
  phone,
  donationFor,
  user,
  monthlyAmount,
  setMonthlyAmount,
  numberOfPayments,
  setNumberOfPayments,
  setPreservedMonthlyAmount,
  setPreservedNumberOfPayments,
  setPreservedIsForever,
  setPreservedOneTimeAmount,
  setPreservedDonationType,
  setSubscriptionData,
  setPreservedSubscriptionDataFromParent,
  setClientSecret,
  setShowPaymentElement,
  setError,
  setLoading,
}) => {
  // For "forever" subscriptions or payments > 36, use the old PaymentIntent approach
  setLoading(true);
  try {
    // Ensure numberOfPayments is sent as a number (not string) when not forever
    const numberOfPaymentsToSend = forever ? 'forever' : (typeof payments === 'number' ? payments : parseInt(payments, 10));

    const foreverRequestBody = {
      monthlyAmount: monthlyAmt,
      numberOfPayments: numberOfPaymentsToSend,
      firstName,
      lastName,
      email,
      ...(phone && phone.trim() && { phone: phone.trim() }),
    };

    // Include donationFor for public users (replaces "Faith Insight Donation")
    if (!user && donationFor && donationFor.trim()) {
      foreverRequestBody.donationFor = donationFor.trim();
    }

    const response = await api.publicRequest('/payments/create-donation', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(user && { Authorization: `Bearer ${localStorage.getItem('faithinsight_token')}` })
      },
      body: JSON.stringify(foreverRequestBody)
    });

    if (response.success) {
      // Check if we have a clientSecret for payment collection (subscription/subscription schedule)
      const clientSecret = response.subscription?.clientSecret || response.subscriptionSchedule?.clientSecret;

      if (clientSecret) {
        // IMPORTANT: Preserve monthly donation values BEFORE showing payment element
        // This ensures the values are available when the form re-renders
        // Use the values we already calculated (may include preserved values)
        const preservedAmount = monthlyAmt;
        const preservedPayments = forever ? 'forever' : (typeof payments === 'number' ? payments : parseInt(payments));

        // Set preserved values first - CRITICAL: These must be set before showing payment element
        setPreservedMonthlyAmount(preservedAmount);
        setPreservedNumberOfPayments(preservedPayments);
        setPreservedIsForever(forever);
        setPreservedOneTimeAmount(null); // Clear one-time amount
        // Preserve donation type to prevent reset on Elements remount
        setPreservedDonationType('monthly');

        // Also ensure form state values are set (they should already be set, but ensure they're not empty)
        if (!monthlyAmount || monthlyAmount === '' || parseFloat(monthlyAmount) !== preservedAmount) {
          setMonthlyAmount(preservedAmount.toString());
        }
        if (!numberOfPayments || numberOfPayments === '' || (numberOfPayments !== preservedPayments.toString() && numberOfPayments !== preservedPayments)) {
          if (!forever && preservedPayments !== 'forever') {
            setNumberOfPayments(preservedPayments.toString());
          }
        }

        // Now store subscription data in BOTH local and parent state (survives remounts)
        const subscriptionDataToStore = response.subscription || response.subscriptionSchedule;
        setSubscriptionData(subscriptionDataToStore);
        setPreservedSubscriptionDataFromParent(subscriptionDataToStore);

        setClientSecret(clientSecret);
        setShowPaymentElement(true);

        setLoading(false);
        return; // Wait for user to complete payment
      } else {
        // No client secret - subscription might have been created without payment (shouldn't happen)
        setError('Failed to create payment method collection. Please try again.');
        setLoading(false);
        return;
      }
    } else {
      setError(response.message || 'Failed to create donation. Please try again.');
      setLoading(false);
    }
  } catch (err) {
    setError(err.message || 'An error occurred. Please try again.');
    setLoading(false);
  }
};
